"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import LegalStamp from "./legal-stamp";

const AGE_GATE_KEY = "cp_age_gate_verified";

function getVerified(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(AGE_GATE_KEY) === "true";
  } catch {
    return false;
  }
}

function setVerified(value: boolean) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(AGE_GATE_KEY, value ? "true" : "false");
  } catch {
    // no-op
  }
}

function WantedStar({ size = 10 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none" aria-hidden="true">
      <path
        d="M6 0.5L7.4 4.4H11.5L8.2 6.8L9.5 10.7L6 8.3L2.5 10.7L3.8 6.8L0.5 4.4H4.6L6 0.5Z"
        fill="#e8620a"
      />
    </svg>
  );
}

export default function AgeGate() {
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    setMounted(true);
    const verified = getVerified();
    setVisible(!verified);
    // small delay to trigger CSS transition
    const timer = window.setTimeout(() => setAnimateIn(!verified), 30);
    return () => window.clearTimeout(timer);
  }, []);

  const handleAccept = () => {
    setVerified(true);
    setAnimateIn(false);
    window.setTimeout(() => setVisible(false), 400);
  };

  // Prevent layout shift / flash: render nothing until mounted
  if (!mounted || !visible) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center overflow-y-auto px-4 py-8"
      style={{ background: "rgba(13,10,7,0.96)", backdropFilter: "blur(6px)" }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="age-gate-title"
    >
      {/* Background atmosphere */}
      <div className="pointer-events-none absolute inset-0" aria-hidden="true">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,110,40,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,110,40,0.04) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div
          className="absolute bottom-0 left-1/2 h-[60%] w-[140%] -translate-x-1/2"
          style={{
            background:
              "radial-gradient(ellipse 70% 55% at 50% 100%, rgba(232,98,10,0.12) 0%, transparent 70%)",
          }}
        />
      </div>

      <div
        className="relative w-full max-w-md transition-all duration-500"
        style={{
          opacity: animateIn ? 1 : 0,
          transform: animateIn ? "translateY(0) scale(1)" : "translateY(18px) scale(0.98)",
        }}
      >
        {/* Card */}
        <div
          className="relative border px-6 py-8 sm:px-8 sm:py-10"
          style={{
            background: "rgba(13,10,7,0.92)",
            borderColor: "rgba(232,98,10,0.35)",
            clipPath: "polygon(14px 0%, 100% 0%, calc(100% - 14px) 100%, 0% 100%)",
          }}
        >
          {/* Corner brackets */}
          <span className="absolute left-3 top-3 h-4 w-4 border-l border-t border-[#e8620a]/45" />
          <span className="absolute right-3 top-3 h-4 w-4 border-r border-t border-[#e8620a]/45" />
          <span className="absolute bottom-3 left-3 h-4 w-4 border-b border-l border-[#e8620a]/45" />
          <span className="absolute bottom-3 right-3 h-4 w-4 border-b border-r border-[#e8620a]/45" />

          {/* Stamp + dossier header */}
          <div className="mb-6 flex flex-col items-center gap-4">
            <LegalStamp size={80} pulse />
            <div className="flex items-center gap-2">
              <WantedStar size={8} />
              <span
                className="text-[9px] font-bold uppercase tracking-[0.28em] text-[#e8620a]/70"
                style={{ fontFamily: "var(--cp-font-barlow-condensed)" }}
              >
                Dossier Confidentiel — Accès Réglementé
              </span>
              <WantedStar size={8} />
            </div>
          </div>

          <h1
            id="age-gate-title"
            className="mb-3 text-center uppercase leading-[0.92] text-white"
            style={{ fontFamily: "var(--cp-font-anton)", fontSize: "clamp(36px, 10vw, 52px)" }}
          >
            Cartel<br />
            <span style={{ color: "#e8620a" }}>Paradise</span>
          </h1>

          <p
            className="mx-auto mb-6 max-w-xs text-center text-white/50"
            style={{ fontFamily: "var(--cp-font-barlow-condensed)", fontSize: 15, lineHeight: 1.55 }}
          >
            Ce site présente des cocktails alcoolisés (12,5&nbsp;%&nbsp;alc./vol.) destinés à un public majeur.
          </p>

          <div
            className="mb-6 border px-4 py-3 text-center"
            style={{
              background: "rgba(232,98,10,0.06)",
              borderColor: "rgba(232,98,10,0.22)",
              clipPath: "polygon(8px 0%, 100% 0%, calc(100% - 8px) 100%, 0% 100%)",
            }}
          >
            <p
              className="text-[12px] font-semibold uppercase tracking-[0.12em] text-white/75"
              style={{ fontFamily: "var(--cp-font-barlow-condensed)" }}
            >
              L&apos;abus d&apos;alcool est dangereux pour la santé
            </p>
            <p
              className="text-[11px] uppercase tracking-[0.1em] text-[#e8620a]/80"
              style={{ fontFamily: "var(--cp-font-barlow-condensed)" }}
            >
              À consommer avec modération
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={handleAccept}
              className="w-full bg-[#e8620a] px-6 py-4 text-center font-bold uppercase tracking-[0.18em] text-[#0d0a07] transition-all hover:bg-[#ff7b26] active:scale-[0.98]"
              style={{
                fontFamily: "var(--cp-font-barlow-condensed)",
                fontSize: 13,
                clipPath: "polygon(10px 0%, 100% 0%, calc(100% - 10px) 100%, 0% 100%)",
              }}
            >
              J&apos;ai plus de 18 ans — Entrer
            </button>

            <Link
              href="/age-refuse"
              className="block py-2 text-center text-[11px] font-semibold uppercase tracking-[0.16em] text-white/35 transition-colors hover:text-white/60"
              style={{ fontFamily: "var(--cp-font-barlow-condensed)" }}
            >
              Je n&apos;ai pas 18 ans
            </Link>
          </div>
        </div>

        {/* Footer micro-copy */}
        <p
          className="mt-5 text-center text-[9px] uppercase tracking-[0.14em] text-white/20"
          style={{ fontFamily: "var(--cp-font-geist-mono), monospace" }}
        >
          Vente d&apos;alcool interdite aux mineurs —{" "}
          <Link
            href="/mentions-legales"
            className="text-[#e8620a]/60 transition-colors hover:text-[#e8620a] hover:underline underline-offset-2"
          >
            Mentions légales
          </Link>
        </p>
      </div>
    </div>
  );
}
